import { Link, useNavigate } from "react-router";
import { Controller } from "react-hook-form";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatCurrencyEUR } from "@/lib/utils";
import { SaveStatusIndicator } from "../forms/save-status-indicator";
import type { SaveStatus } from "../forms/use-form-runtime";
import { useMdfRuntime } from "./use-mdf-runtime";
import { useDeleteMdfDraft } from "./use-delete-mdf-draft";
import { MdfAttachmentField } from "./mdf-attachment-field";

// Editable branch of mdf-detail-page. Renders the 5-field MDF form via
// react-hook-form (orchestrated by useMdfRuntime which wires 500ms-debounced
// auto-save) plus the multi-file attachment affordance, plus submit/discard
// footer. Mirrors sub-bucket 2's form-detail-page shape but with a fixed
// schema (no field registry).
//
// Allocation cannot be changed after draft creation (the chooser on /new
// picked it; the runtime hook hydrates the read-only allocation balance
// from the same /drafts/:id endpoint).

type FieldProps = {
  label: string;
  required?: boolean;
  help?: string;
  error?: string | undefined;
  children: React.ReactNode;
};

function Field({ label, required, help, error, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children}
      {help && !error ? (
        <p className="text-xs text-muted-foreground">{help}</p>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

export function MdfDraftEditView({ requestId }: { requestId: string }) {
  const navigate = useNavigate();
  const runtime = useMdfRuntime(requestId);
  const deleteDraft = useDeleteMdfDraft();
  const { form, draftQuery, save, submit } = runtime;

  if (draftQuery.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (draftQuery.isError || !draftQuery.data) {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border bg-card p-8 text-center">
        <h2 className="font-display text-xl font-semibold tracking-tight">
          Couldn&rsquo;t load this draft
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Please refresh the page. If the problem persists, contact support.
        </p>
        <Link
          to="/portal/mdf"
          className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to marketing funds
        </Link>
      </div>
    );
  }

  const detail = draftQuery.data;
  const balance = detail.allocation.balance_amount_eur;

  const status: SaveStatus = save.isPending
    ? "saving"
    : save.isError
      ? "error"
      : save.isSuccess
        ? "saved"
        : "idle";

  const amountValue = form.watch("amount_eur");
  const exceedsBalance =
    typeof amountValue === "number" && amountValue > balance;

  function onSubmit(values: {
    amount_eur?: number | null;
    campaign_name?: string | null;
    campaign_type?: string | null;
    description?: string | null;
  }) {
    if (
      typeof values.amount_eur !== "number" ||
      !values.campaign_name ||
      values.campaign_name.trim().length === 0
    ) {
      toast.error("Add a campaign name and an amount before submitting.");
      return;
    }
    if (values.amount_eur > balance) {
      form.setError("amount_eur", {
        message: `Amount exceeds the available balance of ${formatCurrencyEUR(balance)}.`,
      });
      return;
    }
    submit.mutate(
      {
        allocation_id: detail.draft.allocation_id,
        amount_eur: values.amount_eur,
        campaign_name: values.campaign_name,
        campaign_type: values.campaign_type ?? null,
        description: values.description ?? null,
        attachment_ids: detail.attachments.map((a) => a.id),
      },
      {
        onSuccess: () => {
          toast.success("Request submitted for review.");
          // Stay on the same URL; the detail page re-fetches and renders the
          // read-only branch since status flips to 'pending'.
          void runtime.draftQuery.refetch();
        },
      },
    );
  }

  function onDiscard() {
    if (!confirm("Discard this draft? This cannot be undone.")) return;
    deleteDraft.mutate(
      { id: requestId },
      {
        onSuccess: () => {
          toast.success("Draft discarded");
          navigate("/portal/mdf", { replace: true });
        },
      },
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to="/portal/mdf"
        className="text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        ← Back to marketing funds
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            Edit request
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Auto-saves as you type. Submit when you&rsquo;re ready for review.
          </p>
        </div>
        <SaveStatusIndicator status={status} className="mt-1 shrink-0" />
      </div>

      {/* Allocation summary — read-only at this stage. */}
      <div className="mt-6 rounded-xl border bg-muted/30 p-4">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Drawing against
        </p>
        <p className="mt-1 text-sm font-medium text-foreground">
          Allocation balance: {formatCurrencyEUR(balance)} available
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {formatCurrencyEUR(detail.allocation.used_amount_eur)} used of{" "}
          {formatCurrencyEUR(detail.allocation.cap_amount_eur)}
        </p>
      </div>

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="mt-6 space-y-5"
      >
        <Controller
          control={form.control}
          name="campaign_name"
          render={({ field, fieldState }) => (
            <Field
              label="Campaign name"
              required
              help="A short, recognisable name for this initiative."
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                value={field.value ?? ""}
                placeholder="e.g. LinkedIn ads pilot — Q3"
                maxLength={200}
              />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="campaign_type"
          render={({ field, fieldState }) => (
            <Field
              label="Campaign type"
              help="Optional. Used to categorise spend (e.g. Webinar, Trade show, Paid ads)."
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                value={field.value ?? ""}
                placeholder="e.g. Paid ads"
              />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="amount_eur"
          render={({ field, fieldState }) => (
            <Field
              label="Amount (EUR)"
              required
              help={`Whole EUR. Available balance: ${formatCurrencyEUR(balance)}.`}
              error={
                fieldState.error?.message ??
                (exceedsBalance
                  ? `Amount exceeds available balance (${formatCurrencyEUR(balance)}).`
                  : undefined)
              }
            >
              <Input
                inputMode="numeric"
                type="number"
                min={1}
                step={1}
                value={field.value ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  field.onChange(v === "" ? null : Number(v));
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                className={cn(
                  "tabular-nums",
                  exceedsBalance ? "border-destructive" : null,
                )}
              />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <Field
              label="Description"
              help="What are you running, who's the audience, and what outcome are you targeting?"
              error={fieldState.error?.message}
            >
              <Textarea
                {...field}
                value={field.value ?? ""}
                rows={4}
                maxLength={2000}
              />
            </Field>
          )}
        />

        <MdfAttachmentField
          requestId={requestId}
          attachments={detail.attachments}
          disabled={submit.isPending || deleteDraft.isPending}
        />

        <div className="flex items-center justify-between border-t pt-5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={
              deleteDraft.isPending || submit.isPending || save.isPending
            }
            className="text-muted-foreground"
          >
            {deleteDraft.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Discard draft
          </Button>
          <Button
            type="submit"
            disabled={
              submit.isPending ||
              save.isPending ||
              exceedsBalance ||
              !form.watch("campaign_name") ||
              typeof form.watch("amount_eur") !== "number"
            }
          >
            {submit.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Submit for review
          </Button>
        </div>
      </form>
    </div>
  );
}
